import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl: string;
  ogType?: "website" | "article";
  ogImage?: string | null;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
}

const SITE_NAME = "Taias";
const SITE_URL = "https://taias.xyz";
const DEFAULT_OG_IMAGE = `${SITE_URL}/taias.png`;

/**
 * SEOHead component for setting meta tags dynamically.
 * Uses useEffect to update document head since we're in a SPA.
 */
export function SEOHead({
  title,
  description,
  canonicalUrl,
  ogType = "website",
  ogImage,
  publishedTime,
  modifiedTime,
  tags = [],
}: SEOHeadProps) {
  useEffect(() => {
    // Set page title
    document.title = `${title} | ${SITE_NAME}`;

    // Helper to set or create meta tag
    const setMetaTag = (
      name: string,
      content: string,
      attribute: "name" | "property" = "name",
    ) => {
      let element = document.querySelector(
        `meta[${attribute}="${name}"]`,
      ) as HTMLMetaElement | null;

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper to set or create link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(
        `link[rel="${rel}"]`,
      ) as HTMLLinkElement | null;

      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Basic meta tags
    setMetaTag("description", description);
    setLinkTag("canonical", canonicalUrl);

    // Open Graph tags
    setMetaTag("og:title", title, "property");
    setMetaTag("og:description", description, "property");
    setMetaTag("og:type", ogType, "property");
    setMetaTag("og:url", canonicalUrl, "property");
    setMetaTag("og:image", ogImage || DEFAULT_OG_IMAGE, "property");
    setMetaTag("og:site_name", SITE_NAME, "property");

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // Article-specific tags
    if (ogType === "article") {
      if (publishedTime) {
        setMetaTag("article:published_time", publishedTime, "property");
      }
      if (modifiedTime) {
        setMetaTag("article:modified_time", modifiedTime, "property");
      }
      tags.forEach((tag, index) => {
        setMetaTag(`article:tag:${index}`, tag, "property");
      });
    }

    // Cleanup function to reset title on unmount (optional)
    return () => {
      document.title = SITE_NAME;
    };
  }, [
    title,
    description,
    canonicalUrl,
    ogType,
    ogImage,
    publishedTime,
    modifiedTime,
    tags,
  ]);

  // JSON-LD structured data
  const structuredData =
    ogType === "article"
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: title,
          description: description,
          image: ogImage || DEFAULT_OG_IMAGE,
          datePublished: publishedTime,
          dateModified: modifiedTime || publishedTime,
          keywords: tags.length > 0 ? tags.join(", ") : undefined,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": canonicalUrl,
          },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/taias.png`,
            },
          },
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_URL,
        };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

export default SEOHead;
